<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { api } from '../../framework/js/api.js';
    import type { KnowledgeSourceItem, KnowledgeValidationResult } from '../admin/knowledge/knowledgeAdmin';
    import { TEAM_SOURCE_TYPES, teamTypeForKind, type TeamSourceMode } from './teamKnowledge';

    export let open = false;
    export let saving = false;
    export let editing: KnowledgeSourceItem | null = null;

    const dispatch = createEventDispatcher<{
        close: void;
        save: {
            title: string;
            body_text: string;
            content_kind: string;
            file: File | null;
            allow_override: boolean;
        };
    }>();

    let title = '';
    let bodyText = '';
    let mode: TeamSourceMode = 'document';
    let file: File | null = null;
    let fileInput: HTMLInputElement | null = null;
    let checking = false;
    let validation: KnowledgeValidationResult | null = null;
    let proceedConfirmed = false;
    let validationError = '';

    $: isEdit = editing !== null;
    $: selectedType = TEAM_SOURCE_TYPES.find((t) => t.mode === mode) ?? TEAM_SOURCE_TYPES[0];
    $: canSave =
        title.trim().length > 0 &&
        !checking &&
        !saving &&
        (isEdit
            ? true
            : mode === 'document'
              ? file !== null
              : bodyText.trim().length > 0) &&
        (!validation?.needs_confirmation || proceedConfirmed) &&
        !validation?.block_upload;

    function reset(): void {
        title = '';
        bodyText = '';
        mode = 'document';
        file = null;
        validation = null;
        proceedConfirmed = false;
        validationError = '';
        if (fileInput) fileInput.value = '';
    }

    $: if (open && editing) {
        title = editing.title;
        bodyText = editing.body_text ?? '';
        const type = teamTypeForKind(editing.content_kind);
        mode = type.mode;
        file = null;
        validation = null;
        proceedConfirmed = false;
        validationError = '';
    }

    $: if (open && !editing) {
        reset();
    }

    function close(): void {
        if (saving || checking) return;
        dispatch('close');
    }

    function onFileChange(event: Event): void {
        const input = event.currentTarget as HTMLInputElement;
        file = input.files?.[0] ?? null;
        validation = null;
        proceedConfirmed = false;
    }

    function onContentChange(): void {
        validation = null;
        proceedConfirmed = false;
    }

    function selectMode(next: TeamSourceMode): void {
        if (isEdit) return;
        mode = next;
        validation = null;
        proceedConfirmed = false;
    }

    async function checkContent(): Promise<KnowledgeValidationResult | null> {
        const textPayload = bodyText.trim() || (isEdit ? (editing?.body_text ?? '') : '');
        if (mode === 'document' && !file && !textPayload) {
            return null;
        }
        if (mode === 'notes' && !textPayload) {
            return null;
        }

        checking = true;
        validationError = '';
        try {
            const formData = new FormData();
            if (textPayload) formData.append('body_text', textPayload);
            formData.append('content_kind', selectedType.contentKind);
            if (file) formData.append('file', file);

            const response: any = await api.validateTeamKnowledgeContent(formData);
            if (response?.success && response.data) {
                return response.data as KnowledgeValidationResult;
            }
            validationError = response?.message || 'Could not check content.';
            return null;
        } catch (error) {
            console.error(error);
            validationError = 'Could not check content. Try again.';
            return null;
        } finally {
            checking = false;
        }
    }

    async function handleSave(): Promise<void> {
        if (!canSave && validation) return;

        if (!validation && !isEdit) {
            validation = await checkContent();
            if (validation?.needs_confirmation || validation?.block_upload) {
                return;
            }
            if (validationError) return;
        }

        if (validation?.block_upload) return;
        if (validation?.needs_confirmation && !proceedConfirmed) return;

        dispatch('save', {
            title: title.trim(),
            body_text: bodyText.trim(),
            content_kind: selectedType.contentKind,
            file,
            allow_override: proceedConfirmed,
        });
    }
</script>

{#if open}
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
        class="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-knowledge-form-title"
        tabindex="-1"
        on:keydown={(e) => e.key === 'Escape' && close()}
    >
        <button type="button" class="modal-backdrop" aria-label="Close" on:click={close}></button>
        <div class="modal-content">
            <header class="modal-header">
                <h2 id="team-knowledge-form-title">{isEdit ? 'Edit item' : 'Add to Team Knowledge'}</h2>
                <button type="button" class="icon-btn" aria-label="Close" on:click={close}>
                    <i class="close icon" aria-hidden="true"></i>
                </button>
            </header>

            <div class="modal-body">
                {#if !isEdit}
                    <fieldset class="type-fieldset">
                        <legend>What are you adding?</legend>
                        <div class="type-row">
                            {#each TEAM_SOURCE_TYPES as type (type.mode)}
                                <button
                                    type="button"
                                    class="type-btn"
                                    class:selected={mode === type.mode}
                                    on:click={() => selectMode(type.mode)}
                                >
                                    <i class="{type.icon} icon" aria-hidden="true"></i>
                                    <span class="type-label">{type.label}</span>
                                    <span class="type-desc">{type.description}</span>
                                </button>
                            {/each}
                        </div>
                    </fieldset>
                {/if}

                <div class="field">
                    <label for="tk-title">Title</label>
                    <input
                        id="tk-title"
                        type="text"
                        bind:value={title}
                        placeholder={mode === 'document' ? 'e.g. Q3 brand tracker brief' : 'e.g. APAC market context'}
                    />
                </div>

                {#if mode === 'document' && !isEdit}
                    <div class="field">
                        <label for="tk-file">File</label>
                        <input
                            id="tk-file"
                            type="file"
                            bind:this={fileInput}
                            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                            on:change={onFileChange}
                        />
                        <p class="hint">PDF, Word, Excel, PowerPoint, or text files</p>
                    </div>
                {:else if mode === 'notes' || (isEdit && bodyText)}
                    <div class="field">
                        <label for="tk-notes">Notes</label>
                        <textarea
                            id="tk-notes"
                            rows="6"
                            bind:value={bodyText}
                            on:input={onContentChange}
                            placeholder="Key facts the AI should know about your team, products, or market…"
                        ></textarea>
                    </div>
                {:else if isEdit && editing?.file_name}
                    <p class="hint">File: {editing.file_name}. Delete and add again to replace the file.</p>
                {/if}

                {#if checking}
                    <p class="status-line" role="status">
                        <i class="circle notch loading icon" aria-hidden="true"></i>
                        Checking content…
                    </p>
                {:else if validationError}
                    <p class="status-line error" role="alert">{validationError}</p>
                {:else if validation}
                    {#if validation.warnings.length > 0}
                        <div class="warn-box" role="alert">
                            {#each validation.warnings as warning (warning.code)}
                                <p>{warning.message}</p>
                            {/each}
                        </div>
                    {/if}
                    {#if validation.needs_confirmation && !validation.block_upload}
                        <label class="confirm-label">
                            <input type="checkbox" bind:checked={proceedConfirmed} />
                            I understand — add this anyway
                        </label>
                    {/if}
                {/if}
            </div>

            <footer class="modal-footer">
                <button type="button" class="btn secondary" on:click={close} disabled={saving || checking}>
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn primary"
                    on:click={handleSave}
                    disabled={!title.trim() || saving || checking || (validation?.block_upload ?? false)}
                >
                    {#if saving}
                        Saving…
                    {:else if checking}
                        Checking…
                    {:else if validation?.needs_confirmation && !proceedConfirmed}
                        Review warning
                    {:else if !validation && !isEdit}
                        Check &amp; save
                    {:else}
                        Save
                    {/if}
                </button>
            </footer>
        </div>
    </div>
{/if}

<style>
    .modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-4);
    }

    .modal-backdrop {
        position: absolute;
        inset: 0;
        border: 0;
        background: var(--shade-background-color);
        cursor: pointer;
    }

    .modal-content {
        position: relative;
        width: min(520px, 100%);
        max-height: 90vh;
        overflow: auto;
        background: var(--white);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-modal);
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-5);
        border-bottom: 1px solid var(--border-color);
    }

    .modal-header h2 {
        margin: 0;
        font-size: var(--text-xl);
        font-weight: 500;
        color: var(--grey);
    }

    .icon-btn {
        border: 0;
        background: transparent;
        cursor: pointer;
        color: var(--grey-secondary);
        padding: var(--space-2);
        border-radius: var(--radius-button);
    }

    .icon-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .modal-body {
        padding: var(--space-5);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
    }

    .type-fieldset {
        border: 0;
        margin: 0;
        padding: 0;
    }

    legend {
        font-weight: 400;
        color: var(--grey);
        margin-bottom: var(--space-2);
    }

    .type-row {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--space-3);
    }

    .type-btn {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--space-1);
        text-align: left;
        padding: var(--space-4);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-card);
        background: var(--white);
        cursor: pointer;
        font: inherit;
        color: var(--grey);
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .type-btn.selected {
        border-color: var(--primary);
        box-shadow: var(--focus-ring);
    }

    .type-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .type-label {
        font-weight: 500;
    }

    .type-desc {
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
    }

    label {
        font-weight: 400;
        color: var(--grey);
    }

    input[type='text'],
    textarea {
        width: 100%;
        box-sizing: border-box;
        padding: 0.75rem 1rem;
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-button);
        background: rgb(var(--input-bg));
        font: inherit;
    }

    input:focus-visible,
    textarea:focus-visible {
        outline: none;
        border-color: var(--primary);
        box-shadow: var(--focus-ring);
    }

    .hint {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .status-line {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .status-line.error {
        color: var(--status-danger-ink);
    }

    .warn-box {
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-button);
        background: var(--status-warn-soft);
        border: 1px solid var(--status-warn);
    }

    .warn-box p {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey);
    }

    .warn-box p + p {
        margin-top: var(--space-2);
    }

    .confirm-label {
        display: flex;
        align-items: flex-start;
        gap: var(--space-2);
        font-size: var(--text-sm);
        color: var(--grey);
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        padding: var(--space-4) var(--space-5);
        border-top: 1px solid var(--border-color);
    }

    .btn {
        border: 0;
        border-radius: var(--border-radius-button);
        padding: var(--space-2) var(--space-4);
        font-weight: 500;
        cursor: pointer;
    }

    .btn.primary {
        background: var(--primary);
        color: var(--white);
    }

    .btn.secondary {
        background: var(--greyish);
        color: var(--grey);
    }

    .btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    @media (max-width: 768px) {
        .type-row {
            grid-template-columns: 1fr;
        }
    }
</style>
